import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router';
import { useCart, maxForSnapshot, itemKey, isPackItem } from '@/features/cart/CartContext';
import { CartPackLine } from '@/features/cart/CartPackLine';
import { useUI } from '@/client/ui/UIContext';
import { useI18n } from '@/client/i18n/I18nContext';
import { CloseIcon } from '@/components/Icons';
import { formatMoney } from '@/lib/money';
import { lp } from '@/client/i18n/locale';

export function CartDrawer() {
  const { cartOpen, closeCart } = useUI();
  const { items, subtotalCents, currency, setQty, remove } = useCart();
  const { t } = useI18n();
  const navigate = useNavigate();

  useEffect(() => {
    if (cartOpen) {
      const onKey = (e: KeyboardEvent) => e.key === 'Escape' && closeCart();
      document.addEventListener('keydown', onKey);
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('keydown', onKey);
        document.body.style.overflow = '';
      };
    }
    return;
  }, [cartOpen, closeCart]);

  if (!cartOpen) return null;

  return (
    <>
      <div className="overlay" onClick={closeCart} aria-hidden />
      <aside className="drawer" role="dialog" aria-modal="true" aria-label={t('a11y.cart')}>
        <div className="drawer__head">
          <strong style={{ fontFamily: 'var(--font-serif)', fontSize: 'var(--fs-lg)' }}>
            {t('cart.title')}
          </strong>
          <button type="button" className="icon-btn" aria-label={t('a11y.menuClose')} onClick={closeCart}>
            <CloseIcon />
          </button>
        </div>

        <div className="drawer__body">
          {items.length === 0 ? (
            <p className="text-muted">{t('cart.empty')}</p>
          ) : (
            items.map((line) =>
              isPackItem(line) ? (
                <CartPackLine key={itemKey(line)} line={line} onNavigate={closeCart} />
              ) : (
              <div className="cart-line" key={itemKey(line)}>
                <Link
                  to={lp(`/shop/${line.product.category}/${line.product.slug}`)}
                  className="cart-line__media"
                  onClick={closeCart}
                >
                  {line.product.image ? (
                    <img src={line.product.image} alt={line.product.name} />
                  ) : null}
                </Link>
                <div>
                  {line.product.brand ? (
                    <span className="card__brand">{line.product.brand}</span>
                  ) : null}
                  <p style={{ fontFamily: 'var(--font-serif)' }}>{line.product.name}</p>
                  {line.product.size ? (
                    <span className="cart-line__size">
                      {t('cart.size')} {line.product.size}
                    </span>
                  ) : null}
                  {line.product.preorder ? (
                    <span className="card__preorder" style={{ display: 'block', marginTop: 'var(--space-1)' }}>
                      {t('product.preorderBadge')}
                    </span>
                  ) : null}
                  <div className="qty" style={{ marginTop: 'var(--space-2)' }}>
                    <button
                      type="button"
                      aria-label={t('a11y.qtyDecrease')}
                      onClick={() => setQty(itemKey(line), line.quantity - 1)}
                    >
                      −
                    </button>
                    <span>{line.quantity}</span>
                    <button
                      type="button"
                      aria-label={t('a11y.qtyIncrease')}
                      disabled={line.quantity >= maxForSnapshot(line.product)}
                      onClick={() => setQty(itemKey(line), line.quantity + 1)}
                    >
                      +
                    </button>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="price">
                    {line.product.priceCents > 0
                      ? formatMoney(line.product.priceCents * line.quantity, currency)
                      : '—'}
                  </div>
                  <button
                    type="button"
                    className="link-underline text-muted"
                    style={{ fontSize: 'var(--fs-xs)', marginTop: 'var(--space-2)' }}
                    onClick={() => remove(itemKey(line))}
                  >
                    {t('cart.remove')}
                  </button>
                </div>
              </div>
              ),
            )
          )}
        </div>

        {items.length > 0 ? (
          <div className="drawer__foot">
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{t('cart.subtotal')}</span>
              <strong className="price">{formatMoney(subtotalCents, currency)}</strong>
            </div>
            <p className="text-muted" style={{ fontSize: 'var(--fs-xs)' }}>
              {t('cart.note')}
            </p>
            <button
              type="button"
              className="btn btn--full"
              onClick={() => {
                closeCart();
                navigate(lp('/checkout'));
              }}
            >
              {t('cart.checkout')}
            </button>
          </div>
        ) : null}
      </aside>
    </>
  );
}
